import { z } from "zod"

import { asRoleId, asTenantId, asUserId } from "../ids.js"
import { ALL_PERMISSIONS } from "../permissions.js"

/**
 * Auth-related zod schemas for @app/shared.
 *
 * Used by the engine for request validation (via nestjs-zod) and by
 * the helm for client-side form validation. Branded-ID transforms keep
 * the parsed output nominally typed so downstream code can't mix a
 * `UserId` with a `RoleId`.
 */

// Permission keys are the canonical resource.action strings from
// permissions.ts. z.enum over the readonly tuple keeps the validator
// in lock-step with the registry — adding a permission there flows
// straight into validation, no second registration needed.
export const PermissionKeySchema = z.enum(ALL_PERMISSIONS)
export type PermissionKey = z.infer<typeof PermissionKeySchema>

// --- Branded ID schemas ------------------------------------------------
// Trust-the-validator pattern: validate the raw string is a non-empty
// bounded string, then brand it via the as* constructor. Schemas that
// embed these get nominally-typed IDs in their parsed output.

const BoundedString = (max = 256) => z.string().min(1).max(max)

export const UserIdSchema = BoundedString().transform(asUserId)
export const RoleIdSchema = BoundedString().transform(asRoleId)
export const TenantIdSchema = BoundedString().transform(asTenantId)

// --- Auth primitives ---------------------------------------------------

export const EmailSchema = z.string().email().max(254)

// v1 password floor: 12 chars min. Tighten in v2 with a strength check.
export const PasswordSchema = z.string().min(12).max(256)

// --- Entities ----------------------------------------------------------

export const UserSchema = z.object({
	id: UserIdSchema,
	tenantId: TenantIdSchema,
	email: EmailSchema,
	displayName: BoundedString(128),
	roleIds: z.array(RoleIdSchema),
	isActive: z.boolean(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
})
export type User = z.infer<typeof UserSchema>

export const RoleSchema = z.object({
	id: RoleIdSchema,
	tenantId: TenantIdSchema,
	name: BoundedString(64),
	description: z.string().max(512).optional(),
	permissions: z.array(PermissionKeySchema),
	isSystem: z.boolean(),
	createdAt: z.string().datetime(),
	updatedAt: z.string().datetime(),
})
export type Role = z.infer<typeof RoleSchema>

// --- Auth flow contracts ----------------------------------------------

export const SignInRequestSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
})
export type SignInRequest = z.infer<typeof SignInRequestSchema>

export const MagicLinkRequestSchema = z.object({
	email: EmailSchema,
	redirectTo: z.string().url().optional(),
})
export type MagicLinkRequest = z.infer<typeof MagicLinkRequestSchema>

// Session — what the engine returns after auth and what the helm reads
// via /me. `permissions` is the flattened union resolved from the
// user's roles at login time so route guards don't need to re-walk the
// role graph on every request.
export const SessionSchema = z.object({
	user: UserSchema,
	permissions: z.array(PermissionKeySchema),
	expiresAt: z.string().datetime(),
})
export type Session = z.infer<typeof SessionSchema>
